import { PROPERTIES_SELECTION } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.constants';
import { extractPropertyUidsFromMultiSelect } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.utils';
import { AirbnbCancellationPolicy } from 'models/AirbnbCancellationPolicy';
import { VrboCancellationPolicy } from 'models/Channels';
import {
  AirbnbPolicyEntry,
  BookingDotComPolicyEntry,
  HostfullyPolicyEntry,
  HvmbPolicyEntry,
  PayloadCancellationPolicyEntry,
  SeasonalRuleColor,
  SeasonalRuleDetail,
  SeasonalRuleHvmbPolicyCode,
  SeasonalRulePayload,
  SeasonalRulePolicyChannel,
  VrboCustomPolicyEntry,
  VrboStandardPolicyEntry,
} from 'models/seasonality/SeasonalRule';
import { format } from 'date-fns';
import * as Yup from 'yup';
import { getUTCDate } from 'utils/dateTimeUtils';
import { APPLIES_TO_FIELD_NAME } from './SeasonalRuleAddEditModal.constants';
import { SeasonalRuleFormValues } from './SeasonalRuleAddEditModal.types';

export const seasonalRuleFormSchema = () =>
  Yup.object({
    name: Yup.string().trim().required().min(1).max(100),
    color: Yup.string().oneOf(Object.values(SeasonalRuleColor)).required(),
    startDate: Yup.date().nullable().required(),
    endDate: Yup.date()
      .nullable()
      .required()
      .test(
        'after-start',
        'End date must be after start date',
        (value, ctx) =>
          !ctx.parent.startDate ||
          !value ||
          value.getTime() >= ctx.parent.startDate.getTime(),
      ),
    appliesTo: Yup.string().required(),
    appliesToProperties: Yup.array()
      .of(
        Yup.object({
          isFixed: Yup.boolean().optional(),
          label: Yup.string(),
          value: Yup.string(),
        }),
      )
      .label('properties')
      .when(APPLIES_TO_FIELD_NAME, {
        is: PROPERTIES_SELECTION.ALL_PROPERTIES,
        then: (schema) => schema.optional().nullable(true),
        otherwise: (schema) => schema.min(1).required(),
      }),
    policies: Yup.object({
      hostfullyText: Yup.string().max(1000),
      airbnbCode: Yup.string().nullable(),
      vrboCode: Yup.string().nullable(),
      vrboCustom: Yup.mixed().nullable(),
      bookingDotComCode: Yup.number().nullable(),
      hvmbCode: Yup.string().nullable(),
    }),
  });

export const getFormDefaultValues = (
  rule?: SeasonalRuleDetail,
  currentProperty?: { uid: string; name: string },
): SeasonalRuleFormValues => {
  const appliesTo = rule
    ? rule.applyToAllProperties
      ? PROPERTIES_SELECTION.ALL_PROPERTIES
      : PROPERTIES_SELECTION.SELECTED_PROPERTIES
    : currentProperty
    ? PROPERTIES_SELECTION.SELECTED_PROPERTIES
    : PROPERTIES_SELECTION.ALL_PROPERTIES;

  const appliesToProperties = rule
    ? rule.properties.map((p) => ({
        label: p.name,
        value: p.uid,
        isFixed: p.uid === currentProperty?.uid,
      }))
    : currentProperty
    ? [
        {
          label: currentProperty.name,
          value: currentProperty.uid,
          isFixed: true,
        },
      ]
    : [];

  const hostfully = rule?.cancellationPolicies.find(
    (p): p is HostfullyPolicyEntry =>
      p.channel === SeasonalRulePolicyChannel.HOSTFULLY,
  );
  const airbnb = rule?.cancellationPolicies.find(
    (p): p is AirbnbPolicyEntry =>
      p.channel === SeasonalRulePolicyChannel.AIRBNB,
  );
  const vrbo = rule?.cancellationPolicies.find(
    (p): p is VrboStandardPolicyEntry | VrboCustomPolicyEntry =>
      p.channel === SeasonalRulePolicyChannel.VRBO,
  );
  const isVrboCustom = vrbo?.policyCode === VrboCancellationPolicy.CUSTOM;
  const bookingDotCom = rule?.cancellationPolicies.find(
    (p): p is BookingDotComPolicyEntry =>
      p.channel === SeasonalRulePolicyChannel.BOOKINGDOTCOM,
  );
  const hvmb = rule?.cancellationPolicies.find(
    (p): p is HvmbPolicyEntry => p.channel === SeasonalRulePolicyChannel.HVMB,
  );

  return {
    name: rule?.name ?? '',
    color: rule?.color ?? '',
    startDate: rule?.startDate ? getUTCDate(new Date(rule.startDate)) : null,
    endDate: rule?.endDate ? getUTCDate(new Date(rule.endDate)) : null,
    appliesTo,
    appliesToProperties,
    policies: {
      hostfullyText: hostfully?.policyText ?? '',
      airbnbCode: (airbnb?.policyCode as AirbnbCancellationPolicy) ?? '',
      vrboCode: vrbo?.policyCode ?? '',
      vrboCustom:
        isVrboCustom && vrbo
          ? {
              customTiers: (vrbo as VrboCustomPolicyEntry).customTiers,
              displayLabel: vrbo.displayLabel,
            }
          : null,
      bookingDotComCode: bookingDotCom?.policyCode
        ? Number(bookingDotCom.policyCode)
        : null,
      hvmbCode: (hvmb?.policyCode as SeasonalRuleHvmbPolicyCode) ?? '',
    },
  };
};

export const toPayload = (
  values: SeasonalRuleFormValues,
): SeasonalRulePayload => {
  const applyToAllProperties =
    values.appliesTo === PROPERTIES_SELECTION.ALL_PROPERTIES;
  const propertyUids = applyToAllProperties
    ? undefined
    : extractPropertyUidsFromMultiSelect(
        'appliesToProperties',
        values as unknown as Record<string, unknown>,
      ) ?? [];

  const cancellationPolicies: PayloadCancellationPolicyEntry[] = [];
  const {
    hostfullyText,
    airbnbCode,
    vrboCode,
    vrboCustom,
    bookingDotComCode,
    hvmbCode,
  } = values.policies;

  if (hostfullyText.trim()) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.HOSTFULLY,
      policyText: hostfullyText.trim(),
    });
  }
  if (airbnbCode) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.AIRBNB,
      policyCode: airbnbCode,
    });
  }
  if (vrboCode === VrboCancellationPolicy.CUSTOM && vrboCustom) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.VRBO,
      policyCode: VrboCancellationPolicy.CUSTOM,
      customTiers: vrboCustom.customTiers,
    });
  } else if (vrboCode && vrboCode !== VrboCancellationPolicy.CUSTOM) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.VRBO,
      policyCode: vrboCode,
    });
  }
  if (bookingDotComCode != null) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.BOOKINGDOTCOM,
      policyCode: String(bookingDotComCode),
    });
  }
  if (hvmbCode) {
    cancellationPolicies.push({
      channel: SeasonalRulePolicyChannel.HVMB,
      policyCode: hvmbCode,
    });
  }

  return {
    name: values.name.trim(),
    color: values.color as SeasonalRuleColor,
    startDate: format(values.startDate as Date, 'yyyy-MM-dd'),
    endDate: format(values.endDate as Date, 'yyyy-MM-dd'),
    applyToAllProperties,
    ...(propertyUids !== undefined ? { propertyUids } : {}),
    cancellationPolicies,
  };
};
